import React, { useEffect, useRef } from 'react';
import BubbleAlert from '../icons/BubbleAlert.jsx';
import CrossLarge from '../icons/CrossLarge.jsx';
import './Alert.css';

const STATUS_TOKEN_MAP = {
  error:       'danger',
  warning:     'warning',
  success:     'success',
  information: 'info',
  feature:     'feature',
};

const ARIA_LIVE_MAP = {
  error:       'assertive',
  warning:     'assertive',
  success:     'polite',
  information: 'polite',
  feature:     'polite',
};

export default function Alert({
  status      = 'information',
  alertStyle  = 'lighter',
  size        = 'small',
  message     = '',
  description = '',
  primaryAction   = '',
  secondaryAction = '',
  dismissible  = true,
  toast        = false,
  autoDismiss  = 5000,
  onDismiss    = null,
  onPrimaryAction   = null,
  onSecondaryAction = null,
  className = '',
}) {
  const tokenStatus = STATUS_TOKEN_MAP[status] || 'info';
  const isLarge     = size === 'large';
  const ariaLive    = ARIA_LIVE_MAP[status] || 'polite';

  const timerRef = useRef(null);

  useEffect(() => {
    if (!toast) return;
    if (status === 'error') return;
    if (!autoDismiss || autoDismiss <= 0) return;

    timerRef.current = setTimeout(() => {
      if (onDismiss) onDismiss();
    }, autoDismiss);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [toast, status, autoDismiss, onDismiss]);

  const rootClass = [
    'alert',
    `alert--${size}`,
    toast ? 'alert--toast' : '',
    className,
  ].filter(Boolean).join(' ');

  const statusIconEl = (
    <span className="alert__status-icon" aria-hidden="true">
      <BubbleAlert width={isLarge ? 20 : 16} height={isLarge ? 20 : 16} />
    </span>
  );

  const dismissIconEl = dismissible ? (
    <button className="alert__dismiss" aria-label="Dismiss" onClick={onDismiss} type="button">
      <CrossLarge width={16} height={16} />
    </button>
  ) : null;

  const primaryActionEl = primaryAction ? (
    <button className="alert__action alert__action--primary" onClick={onPrimaryAction} type="button">
      {primaryAction}
    </button>
  ) : null;

  const secondaryActionEl = secondaryAction && isLarge ? (
    <>
      <span className="alert__action-divider" aria-hidden="true">∙</span>
      <button className="alert__action alert__action--secondary" onClick={onSecondaryAction} type="button">
        {secondaryAction}
      </button>
    </>
  ) : null;

  if (isLarge) {
    return (
      <div className={rootClass} data-status={tokenStatus} data-style={alertStyle} role="alert" aria-live={ariaLive}>
        {statusIconEl}
        <div className="alert__content">
          <div className="alert__text">
            {message     && <span className="alert__title">{message}</span>}
            {description && <span className="alert__description">{description}</span>}
          </div>
          {(primaryAction || secondaryAction) && (
            <div className="alert__actions">
              {primaryActionEl}
              {secondaryActionEl}
            </div>
          )}
        </div>
        {dismissIconEl}
      </div>
    );
  }

  return (
    <div className={rootClass} data-status={tokenStatus} data-style={alertStyle} role="alert" aria-live={ariaLive}>
      {statusIconEl}
      <span className="alert__message">{message}</span>
      {primaryActionEl}
      {dismissIconEl}
    </div>
  );
}
